import { Button } from "@mui/material";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import { ISelectType } from "../../../../components/UI/Inputs/AutoCompleteText";
import useFetch from "../../../../hooks/useFetch";
import {
  getTicketAutomationReceivedRuleRescheduleContext,
  ITicketAutomationReceivedRuleRescheduleContext,
} from "../../../../services/tasySchedMenu.service";
import { IScheduleAppointment } from "../../model";
import CancelSchedule from "./CancelSchedule/NewCancelSchedule";

type INewScheduleAppointment = IScheduleAppointment & {
  calendarPreferencesGrouped?: string;
  medicalSpecialtyCode?: number;
};

interface IRescheduleButton {
  tasySchedulingTypeCode: string;
  appointment: INewScheduleAppointment;
  newSchedulePath?: string;
  cancellationReasonOptions?: ISelectType[];
  myAppointmentsRuleId?: string;
}

const NewRescheduleButton: React.FC<IRescheduleButton> = ({
  appointment,
  tasySchedulingTypeCode,
  newSchedulePath = "/schedule/newSchedule",
  cancellationReasonOptions,
  myAppointmentsRuleId,
}) => {
  const { t } = useTranslation();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const navigate = useNavigate();
  const [rescheduleContext, setRescheduleContext] =
    useState<ITicketAutomationReceivedRuleRescheduleContext | null>(null);
  const { sendRequest: getRescheduleContext, loading } = useFetch(
    getTicketAutomationReceivedRuleRescheduleContext,
  );

  const handleCancelled = () => {
    if (rescheduleContext) {
      navigate(`/schedule-new/ruleFlow/${rescheduleContext.ruleId}`, {
        state: {
          rescheduleContext: {
            ...rescheduleContext,
            appointment,
          },
        },
      });
      return;
    }

    navigate(newSchedulePath);
  };

  const handleClick = async () => {
    if (!myAppointmentsRuleId) {
      setOpenCancelModal(true);
      return;
    }

    const { data, success } = await getRescheduleContext({
      myAppointmentsRuleId,
      tasyScheduleTypeCode: tasySchedulingTypeCode,
      calendarIdentifier: String(appointment.calendarIdentifier),
      calendarPreferenceCodes: appointment.calendarPreferencesGrouped ?? "",
      calendarClassificationCode: appointment.calendarClassificationCode ?? "",
      medicalSpecialtyCode: String(appointment.medicalSpecialtyCode ?? ""),
      individualIntegrationId: appointment.pacientIndividualIdentifier,
    });

    if (success && data) {
      setRescheduleContext(data);
      setOpenCancelModal(true);
    }
  };

  return (
    <>
      {tasySchedulingTypeCode && appointment && (
        <CancelSchedule
          data={appointment}
          type={tasySchedulingTypeCode}
          onClose={() => setOpenCancelModal(false)}
          open={openCancelModal}
          onCancelled={handleCancelled}
          cancellationReasonOptions={cancellationReasonOptions}
          reschedule
        />
      )}
      <Button
        startIcon={<TodayRoundedIcon />}
        disabled={loading}
        onClick={handleClick}
      >
        {t("page.schedules.scheduleDetails.reschedule")}
      </Button>
    </>
  );
};

export default memo(NewRescheduleButton);
